import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { apiClient, setAuthFlag } from '@/lib/api-client';
import {
  clearSessionApiResponseCache,
  clearSessionOfflineData,
  confirmDiscardOfflineWork,
  holdOfflineReplay,
  OfflineDiscardCancelledError,
} from '@/lib/offline/session-offline-data';
import { queryKeys } from '@/lib/query-client';
import type { UserMembership, SwitchOrgResponse } from '@/lib/types';

const MEMBERSHIPS_API = '/api/v1/my-memberships/';
const SWITCH_ORG_API = '/api/v1/auth/switch-org/';

export async function handleOrganizationSwitchSuccess(qc: QueryClient): Promise<void> {
  setAuthFlag();
  await clearSessionOfflineData();
  await clearSessionApiResponseCache();
  qc.clear();
}

export function useMemberships() {
  return useQuery({
    queryKey: queryKeys.memberships.list(),
    queryFn: async (): Promise<UserMembership[]> => {
      const response = await apiClient.get<UserMembership[]>(MEMBERSHIPS_API);
      return response.data;
    },
  });
}

export function useSwitchOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (organizationId: number): Promise<SwitchOrgResponse> => {
      if (!(await confirmDiscardOfflineWork('switch-org'))) {
        throw new OfflineDiscardCancelledError();
      }
      const restore = holdOfflineReplay();
      try {
        const response = await apiClient.post<SwitchOrgResponse>(SWITCH_ORG_API, {
          organization_id: organizationId,
        });
        return response.data;
      } catch (error) {
        restore();
        throw error;
      }
    },
    onSuccess: () => handleOrganizationSwitchSuccess(qc),
  });
}
